import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag, Loader2, PauseCircle, PlayCircle } from "lucide-react";
import { cn, FIELD_CLASS } from "@/lib/utils";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { DateCell } from "@/components/admin/DateCell";
import { categoryLabel } from "@/lib/categories";
import {
  REPORT_STATUSES,
  reportReasonLabel,
  reportStatusLabel,
  messageReportReasonLabel,
} from "@/lib/reports";
import {
  adminListReports,
  adminUpdateReport,
  adminSetAdPaused,
  adminListMessageReports,
  adminUpdateMessageReport,
} from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/anmalningar")({
  head: () => ({
    meta: [
      { title: "Anmälningar — Administration | Sellavo" },
      {
        name: "description",
        content: "Hantera anmälda annonser: anledning, kommentar och status.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Anmälningar">
      <ReportsTabs />
    </AdminShell>
  ),
});

function ReportsTabs() {
  const [tab, setTab] = useState<"ads" | "messages">("ads");
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        {[
          { value: "ads" as const, label: "Anmälda annonser" },
          { value: "messages" as const, label: "Anmälda meddelanden" },
        ].map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
              tab === t.value
                ? "bg-primary text-primary-foreground"
                : "text-foreground hover:bg-accent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "ads" ? <ReportsBody /> : <MessageReportsBody />}
    </div>
  );
}

const FILTERS = [
  { value: "new", label: "Nya" },
  ...REPORT_STATUSES.filter((s) => s.value !== "new"),
  { value: "all", label: "Alla" },
];

const STATUS_STYLE: Record<string, string> = {
  new: "bg-destructive/10 text-destructive",
  reviewed: "bg-amber-500/10 text-amber-600",
  actioned: "bg-emerald-500/10 text-emerald-600",
  dismissed: "bg-muted text-muted-foreground",
};

function ReportsBody() {
  const list = useServerFn(adminListReports);
  const update = useServerFn(adminUpdateReport);
  const setPaused = useServerFn(adminSetAdPaused);
  const qc = useQueryClient();
  const [status, setStatus] = useState("new");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin-reports", status],
    queryFn: () => list({ data: { status } }),
  });

  const mutate = useMutation({
    mutationFn: (input: {
      id: string;
      status?: "new" | "reviewed" | "actioned" | "dismissed";
      adminNote?: string;
    }) => update({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
      qc.invalidateQueries({ queryKey: ["admin-inbox-summary"] });
    },
  });

  const pause = useMutation({
    mutationFn: (input: { slug: string; paused: boolean }) => setPaused({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
      qc.invalidateQueries({ queryKey: ["admin-inbox-summary"] });
    },
  });

  const reports = data?.reports ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatus(f.value)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              status === f.value ? "border-primary bg-accent" : "border-border hover:bg-accent/60"
            }`}
          >
            {f.label}
          </button>
        ))}
        {data && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Flag className="h-4 w-4 text-destructive" /> {data.openCount} nya anmälningar
            {"urgentCount" in data && (data as any).urgentCount > 0 && (
              <span className="rounded bg-destructive px-2 py-0.5 text-xs font-semibold text-destructive-foreground">
                {(data as any).urgentCount} allvarliga
              </span>
            )}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : reports.length === 0 ? (
        <p className="rounded-md border border-border p-6 text-sm text-muted-foreground">
          Inga anmälningar i den här vyn.
        </p>
      ) : (
        <ul className="space-y-3">
          {reports.map((r: any) => (
            <li key={r.id} className="rounded-md border border-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status] ?? ""}`}
                    >
                      {reportStatusLabel(r.status)}
                    </span>
                    <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
                      {reportReasonLabel(r.reason)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      <DateCell value={r.created_at} />
                    </span>
                    {(r.ai_priority === "akut" || r.ai_priority === "hog") && (
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-semibold ${
                          r.ai_priority === "akut"
                            ? "bg-destructive text-destructive-foreground"
                            : "bg-amber-500/20 text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        {r.ai_priority === "akut" ? "AI: akut" : "AI: brådskande"}
                      </span>
                    )}
                    {(r.ai_risks ?? []).map((risk: string) => (
                      <span
                        key={risk}
                        className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                      >
                        {risk}
                      </span>
                    ))}
                  </div>
                  {r.ai_summary && (
                    <p className="mt-2 rounded-md bg-muted/60 px-3 py-2 text-sm text-foreground">
                      <span className="font-semibold">AI-bedömning:</span> {r.ai_summary}
                    </p>
                  )}

                  <p className="mt-2 font-heading text-base font-semibold text-foreground">
                    {r.ad?.title ?? "Annonsen är borttagen"}
                  </p>
                  {r.ad && (
                    <p className="text-sm text-muted-foreground">
                      {categoryLabel(r.ad.category ?? "ovrigt")} · {r.ad.location || "okänd ort"} ·{" "}
                      {Number(r.ad.price).toLocaleString("sv-SE")} kr
                      {r.ad.is_paused && " · pausad"}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Anmäld av {r.reporter?.name || r.reporter?.email || "okänd användare"}
                    {r.reporter?.name && r.reporter?.email ? ` (${r.reporter.email})` : ""}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {r.ad && (
                    <>
                      <Link
                        to="/a/$slug"
                        params={{ slug: r.ad.slug }}
                        className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
                      >
                        Visa annons
                      </Link>
                      <Link
                        to="/admin/annons/$slug"
                        params={{ slug: r.ad.slug }}
                        className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
                      >
                        Historik
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pause.isPending}
                        onClick={() => pause.mutate({ slug: r.ad.slug, paused: !r.ad.is_paused })}
                      >
                        {r.ad.is_paused ? (
                          <>
                            <PlayCircle className="mr-1.5 h-4 w-4" /> Återaktivera
                          </>
                        ) : (
                          <>
                            <PauseCircle className="mr-1.5 h-4 w-4" /> Pausa annonsen
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  {r.reporter?.email && r.ad?.user_id && (
                    <Link
                      to="/admin/anvandare/$userId"
                      params={{ userId: r.ad.user_id }}
                      className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
                    >
                      Visa ägare
                    </Link>
                  )}
                </div>
              </div>

              {r.comment && (
                <p className="mt-3 whitespace-pre-line rounded bg-muted/40 p-3 text-sm text-foreground">
                  “{r.comment}”
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {REPORT_STATUSES.map((s) => (
                  <Button
                    key={s.value}
                    size="sm"
                    variant={r.status === s.value ? "default" : "outline"}
                    disabled={mutate.isPending}
                    onClick={() => mutate.mutate({ id: r.id, status: s.value })}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  value={notes[r.id] ?? r.admin_note ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                  placeholder="Intern anteckning"
                  maxLength={2000}
                  className={cn(FIELD_CLASS, "min-w-[220px] flex-1")}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    mutate.isPending || (notes[r.id] ?? r.admin_note ?? "") === (r.admin_note ?? "")
                  }
                  onClick={() => mutate.mutate({ id: r.id, adminNote: notes[r.id] ?? "" })}
                >
                  {mutate.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{" "}
                  Spara anteckning
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(mutate.error instanceof Error || pause.error instanceof Error) && (
        <p className="text-sm text-destructive">
          {(mutate.error as Error)?.message ?? (pause.error as Error)?.message}
        </p>
      )}
    </div>
  );
}

function MessageReportsBody() {
  const list = useServerFn(adminListMessageReports);
  const update = useServerFn(adminUpdateMessageReport);
  const qc = useQueryClient();
  const [status, setStatus] = useState("new");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin-message-reports", status],
    queryFn: () => list({ data: { status } }),
  });

  const mutate = useMutation({
    mutationFn: (input: {
      id: string;
      status?: "new" | "reviewed" | "actioned" | "dismissed";
      adminNote?: string;
    }) => update({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-message-reports"] });
      qc.invalidateQueries({ queryKey: ["admin-inbox-summary"] });
    },
  });

  const reports = data?.reports ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatus(f.value)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              status === f.value ? "border-primary bg-accent" : "border-border hover:bg-accent/60"
            }`}
          >
            {f.label}
          </button>
        ))}
        {data && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Flag className="h-4 w-4 text-destructive" /> {data.openCount} nya anmälningar
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : reports.length === 0 ? (
        <p className="rounded-md border border-border p-6 text-sm text-muted-foreground">
          Inga anmälda meddelanden i den här vyn.
        </p>
      ) : (
        <ul className="space-y-3">
          {reports.map((r: any) => (
            <li key={r.id} className="rounded-md border border-border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status] ?? ""}`}
                >
                  {reportStatusLabel(r.status)}
                </span>
                <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
                  {messageReportReasonLabel(r.reason)}
                </span>
                <span className="text-xs text-muted-foreground">
                  <DateCell value={r.created_at} />
                </span>
              </div>

              <p className="mt-2 text-sm text-foreground">
                <span className="font-semibold">
                  {r.reported?.name || r.reported?.email || "okänd användare"}
                </span>{" "}
                anmäld av {r.reporter?.name || r.reporter?.email || "okänd användare"}
              </p>
              {r.ad && <p className="text-xs text-muted-foreground">Annons: {r.ad.title}</p>}

              {r.message_snapshot && (
                <blockquote className="mt-3 whitespace-pre-line rounded bg-muted/40 p-3 text-sm italic text-foreground">
                  “{r.message_snapshot}”
                </blockquote>
              )}
              {r.comment && (
                <p className="mt-2 whitespace-pre-line rounded border border-border p-3 text-sm text-foreground">
                  Kommentar: {r.comment}
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {r.ad?.slug && (
                  <Link
                    to="/a/$slug"
                    params={{ slug: r.ad.slug }}
                    className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
                  >
                    Visa annons
                  </Link>
                )}
                <Link
                  to="/admin/anvandare/$userId"
                  params={{ userId: r.reported_user_id }}
                  className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
                >
                  Visa anmäld användare
                </Link>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {REPORT_STATUSES.map((s) => (
                  <Button
                    key={s.value}
                    size="sm"
                    variant={r.status === s.value ? "default" : "outline"}
                    disabled={mutate.isPending}
                    onClick={() => mutate.mutate({ id: r.id, status: s.value })}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  value={notes[r.id] ?? r.admin_note ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                  placeholder="Intern anteckning"
                  maxLength={2000}
                  className={cn(FIELD_CLASS, "min-w-[220px] flex-1")}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    mutate.isPending || (notes[r.id] ?? r.admin_note ?? "") === (r.admin_note ?? "")
                  }
                  onClick={() => mutate.mutate({ id: r.id, adminNote: notes[r.id] ?? "" })}
                >
                  {mutate.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{" "}
                  Spara anteckning
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {mutate.error instanceof Error && (
        <p className="text-sm text-destructive">{mutate.error.message}</p>
      )}
    </div>
  );
}
